import React from "react";
import { nanoid } from "nanoid";
import {
  CommunicationFeedbackFollowUp,
  CommunicationFeedbackResponseType,
  CommunicationFeedbackSettings,
} from "@/types/feedback-settings";
import { Plus } from "lucide-react";
import { Label } from "@/components/ui/label";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { SortableList } from "@/components/generic/SortableList";
import { CommunicationFollowUpCard } from "./CommunicationFollowUpCard";

interface CommunicationFeedbackSettingsFormProps {
  settings: CommunicationFeedbackSettings;
  onSettingsChange: (settings: CommunicationFeedbackSettings) => void;
}

const createFollowUp = (order: number): CommunicationFeedbackFollowUp => ({
  clientId: nanoid(),
  title: "",
  order,
  isActive: true,
  minRating: 5,
  maxRating: 5,
  actionType: "SEND_MESSAGE",
  messageTemplate: "",
});

const reorderFollowUps = (
  followUps: CommunicationFeedbackFollowUp[],
): CommunicationFeedbackFollowUp[] =>
  followUps.map((followUp, index) => ({
    ...followUp,
    clientId: followUp.clientId ?? followUp.id ?? nanoid(),
    order: index + 1,
  }));

const getFollowUpKey = (followUp: CommunicationFeedbackFollowUp) =>
  followUp.clientId ?? followUp.id ?? String(followUp.order);

const responseTypes: CommunicationFeedbackResponseType[] = [
  "STARS",
  "RATING",
  "TEXT",
];

export const CommunicationFeedbackSettingsForm = ({
  settings,
  onSettingsChange,
}: CommunicationFeedbackSettingsFormProps): React.JSX.Element => {
  const { t } = useTranslation();

  const handleFollowUpChange = (
    index: number,
    nextFollowUp: CommunicationFeedbackFollowUp,
  ) => {
    onSettingsChange({
      ...settings,
      followUps: reorderFollowUps(
        settings.followUps.map((followUp, currentIndex) =>
          currentIndex === index ? nextFollowUp : followUp,
        ),
      ),
    });
  };

  const handleFollowUpRemove = (index: number) => {
    onSettingsChange({
      ...settings,
      followUps: reorderFollowUps(
        settings.followUps.filter((_, currentIndex) => currentIndex !== index),
      ),
    });
  };

  const handleFollowUpAdd = (index?: number) => {
    const nextFollowUp = createFollowUp(settings.followUps.length + 1);
    const followUps = [...settings.followUps];

    if (index === undefined) {
      followUps.push(nextFollowUp);
    } else {
      followUps.splice(index + 1, 0, nextFollowUp);
    }

    onSettingsChange({
      ...settings,
      followUps: reorderFollowUps(followUps),
    });
  };

  return (
    <div className="flex flex-col gap-6">
      <Label className="flex items-start gap-3">
        <Switch
          checked={settings.enabled}
          onCheckedChange={(enabled) =>
            onSettingsChange({
              ...settings,
              enabled,
            })
          }
        />
        <div className="flex flex-col gap-1">
          <span className="text-base font-medium">
            {t("feedbacks.settings.communication.enabled")}
          </span>
        </div>
      </Label>

      <div className="border-border bg-background rounded-lg border p-4">
        <div className="flex flex-col gap-1">
          <span className="text-base font-medium">
            {t("feedbacks.settings.communication.formSettings")}
          </span>
          <span className="text-muted-foreground text-sm">
            {t("feedbacks.settings.communication.formSettingsHint")}
          </span>
        </div>

        <div className="mt-4 grid gap-4 md:grid-cols-[minmax(0,1fr)_220px]">
          <Label className="flex flex-col gap-2 text-sm font-medium">
            {t("feedbacks.settings.communication.question")}
            <Textarea
              value={settings.question}
              onChange={(event) =>
                onSettingsChange({
                  ...settings,
                  question: event.target.value,
                })
              }
              placeholder={t(
                "feedbacks.settings.communication.questionPlaceholder",
              )}
              className="min-h-24 resize-none"
            />
          </Label>

          <Label className="flex flex-col gap-2 text-sm font-medium">
            {t("feedbacks.settings.communication.responseType")}
            <Select
              value={settings.responseType}
              onValueChange={(value) =>
                onSettingsChange({
                  ...settings,
                  responseType: value as CommunicationFeedbackResponseType,
                })
              }
            >
              <SelectTrigger className="h-10">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {responseTypes.map((responseType) => (
                  <SelectItem key={responseType} value={responseType}>
                    {t(
                      `feedbacks.settings.communication.responseTypes.${responseType}`,
                    )}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Label>
        </div>
      </div>

      <div className="flex flex-col gap-3">
        <div className="flex items-center justify-between gap-3">
          <div className="flex flex-col gap-1">
            <span className="text-base font-medium">
              {t("feedbacks.settings.communication.responses")}
            </span>
            <span className="text-muted-foreground text-sm">
              {t("feedbacks.settings.communication.responsesHint")}
            </span>
          </div>

          <Button
            type="button"
            variant="outline"
            onClick={() => handleFollowUpAdd()}
          >
            <Plus className="size-4" />
            {t("feedbacks.settings.communication.addResponse")}
          </Button>
        </div>

        {settings.followUps.length > 0 ? (
          <SortableList
            items={settings.followUps}
            direction="column"
            keyExtractor={getFollowUpKey}
            onReorder={(followUps) =>
              onSettingsChange({
                ...settings,
                followUps: reorderFollowUps(followUps),
              })
            }
            className="flex flex-col gap-3"
            renderItem={(followUp) => {
              const index = settings.followUps.findIndex(
                (item) => getFollowUpKey(item) === getFollowUpKey(followUp),
              );

              return (
                <CommunicationFollowUpCard
                  followUp={followUp}
                  canRemove={settings.followUps.length > 1}
                  onChange={(nextFollowUp) =>
                    handleFollowUpChange(index, nextFollowUp)
                  }
                  onRemove={() => handleFollowUpRemove(index)}
                  onAddAfter={() => handleFollowUpAdd(index)}
                />
              );
            }}
          />
        ) : (
          <div className="border-border text-muted-foreground rounded-lg border border-dashed p-6 text-sm">
            {t("feedbacks.settings.communication.noResponses")}
          </div>
        )}
      </div>

      <Label className="flex items-start gap-3">
        <Switch
          checked={settings.allowComment}
          onCheckedChange={(allowComment) =>
            onSettingsChange({
              ...settings,
              allowComment,
            })
          }
        />
        <div className="flex flex-col gap-1">
          <span className="text-base font-medium">
            {t("feedbacks.settings.communication.allowComment")}
          </span>
          <span className="text-muted-foreground text-sm">
            {t("feedbacks.settings.communication.allowCommentHint")}
          </span>
        </div>
      </Label>
    </div>
  );
};
